/**
 * shared/feature-flags.ts
 *
 * Flag key registry and evaluation context type shared between
 * server and client. All Wave 1+ features ship behind these keys.
 */

// ---------------------------------------------------------------------------
// Flag key constants — add new flags here as features are introduced
// ---------------------------------------------------------------------------

export const FLAG_KEYS = {
  // state_ledger is excluded from the D-4 prune (#1804) — owned by D-1
  // (retire the whole state-ledger footprint), not decided here.
  STATE_LEDGER: "state_ledger",
  // Duress & Anomaly (Wave 2 Phase B). Only the anomaly/cooling-period family
  // below is excluded from the D-4 prune (#1804) — owned by D-6/F-3. The
  // sibling `duress_mode` key WAS in D-4's scope and was deleted (2026-07-22):
  // duress logic (server/logic/duress/**, duress.routes.ts) runs unconditional
  // and unconditionally-registered — the flag was never read anywhere.
  ANOMALY_DETECTION: "anomaly_detection",
  ANOMALY_THRESHOLD: "anomaly_threshold",
  ANOMALY_AUTO_DURESS_FAILURES: "anomaly_auto_duress_failures",
  ANOMALY_AUTO_DURESS_WINDOW_HOURS: "anomaly_auto_duress_window_hours",
  BASELINE_MINIMUM_DAYS: "baseline_minimum_days",
  COOLING_PERIOD_MIN_HOURS: "cooling_period_min_hours",
  COOLING_PERIOD_MAX_HOURS: "cooling_period_max_hours",
  // External API (Wave 3). Only external_api_v1 is a real, wired gate
  // (server/routes/external-api/index.ts requireFeatureFlag). The sibling
  // external_api_mtls/external_api_rate_limits/org_domain_verification keys
  // were deleted by the D-4 prune (#1804): mTLS (server/middleware/mtls.ts)
  // and per-org rate limiting (server/middleware/org-rate-limit.ts) both
  // already exist and are enforced UNCONDITIONALLY on every v1 route — no
  // flag ever gated them, and wiring an admin kill-switch onto a mandatory
  // security control would be a regression, not a feature. Domain
  // verification (server/logic/external-api/domain-verification.ts) is
  // likewise unconditional. See #1804 PR body triage table for detail.
  EXTERNAL_API_V1: "external_api_v1",
  // Bespoke memory-seeding ceremony (#1221). Server gate: bespoke-seed.routes.ts
  // (BESPOKE_FLAG_KEY) reads the same `bespoke_seeding` key via storage.getFlagByKey.
  BESPOKE_SEEDING: "bespoke_seeding",
  // ID verification onboarding (default OFF in dev/staging/prod)
  ID_VERIFICATION_ONBOARDING: "id_verification_onboarding",
  // Onboarding step toggles — DEFAULT ON / opt-OUT (see use-feature-flag.ts
  // useFeatureFlagWithDefault). With no flag row, both steps stay visible.
  ONBOARDING_SENSORY_VAULT: "onboarding_sensory_vault",
  ONBOARDING_GUARDIAN_CIRCLE: "onboarding_guardian_circle",
  // Pilot-core surfaces (3 Capital launch, Track C / #1407). All 5 default ON
  // in production (seeded by migration 0095) — toggleable OFF per-surface by
  // an operator via PUT /api/admin/flags/:key. Server-enforced at each
  // surface's route registration via server/middleware/require-feature-flag.ts.
  COMMS_HUB: "comms_hub",
  PAYMENT_REQUESTS: "payment_requests",
  DEALS_MONEY_OPS: "deals_money_ops",
  BANK_ACCOUNTS: "bank_accounts",
  ABA_EXPORT: "aba_export",
  // Open (anonymous, no-invite) self-registration gate (#1436). See
  // FLAG_METADATA.self_registration for the fail-closed-in-production /
  // default-ON-in-dev posture.
  SELF_REGISTRATION: "self_registration",
  // IDV attribute-match enforcement (onboarding-identity-binding Module 04,
  // #1523). DEFAULT ON in EVERY environment (defaultWhenMissing) — the INVERSE
  // of self_registration's fail-closed posture: this is a binding control we
  // want armed unless an operator has explicitly created an OFF row (go/no-go
  // kill-switch). A present OFF row skips the match — and that skip is AUDITED,
  // never silent (server/logic/id-verification/assertion-match.ts).
  ID_ASSERTION_MATCH: "id_assertion_match",
  // Master-passkey approval pin client surfaces (device-limit spec PR B,
  // #1605, module 02 §3). DEFAULT ON — server-side enforcement
  // (`requireMasterCredential`) is per-account opt-in regardless of this
  // flag (module 02 §3); the flag gates only the Security Centre UI (badge,
  // designate/transfer/veto flows). Client reads via useFeatureFlagWithDefault
  // with a `true` default, mirroring onboarding_sensory_vault below — a
  // missing row keeps the surfaces visible.
  DEVICE_MASTER_PIN: "device_master_pin",
  // Generic confirm-on-respond reverse-bind (communication-flow-clarity module
  // 04, ledger #9 SCOPE-clause gap). Widens respond-begin.ts's `tookBindingPath`
  // from capital_call_verify-only to ALSO accept a generic `bank_account`
  // challenge issued with the wizard's "confirm on respond" bind mode
  // (bindAtRespond=true). DEFAULT OFF (no defaultWhenMissing) — fail-closed for
  // an unshipped capability; flip only after the real-DB integration smoke
  // (tests/integration/payment-rail-challenges/generic-reverse-bind-smoke.test.ts)
  // is green in CI. Also gates module 03's wizard radio (client-visible).
  GENERIC_CONFIRM_ON_RESPOND_BIND: "generic_confirm_on_respond_bind",
  FREE_FORM_ANSWER_CAPTURE: "free_form_answer_capture",
  ORDERED_CHALLENGE_WORKFLOW_CREATION: "ordered_challenge_workflow_creation",
  APPROVAL_TIMELINE_V2: "approval_timeline_v2",
  // Sole-admin bank-account verification quorum carve-out (#2086, spec module
  // 01 "Feature flag"). DELIBERATELY NO `defaultWhenMissing` — mirrors the
  // GENERIC_CONFIRM_ON_RESPOND_BIND precedent verbatim: a missing OR errored
  // flag-row read is treated as OFF (fail-closed), never ON. While OFF,
  // `markVerifiedIfQuorum` behaves byte-identically to today, the new
  // `bank_accounts.sole_admin_hold_until` column is never written, and the
  // `add_confirm` consent action 404s at the route layer.
  SOLE_ADMIN_BANK_HOLD: "sole_admin_bank_hold",
  // Internal team conversations (internal-team-conversations, spec module 05
  // §Feature flag posture, D17). DELIBERATELY NO `defaultWhenMissing` —
  // mirrors the SOLE_ADMIN_BANK_HOLD / GENERIC_CONFIRM_ON_RESPOND_BIND
  // precedent verbatim: a missing OR errored flag-row read is treated as OFF
  // (fail-closed for an unshipped capability). Gates NEW internal-conversation
  // creation, NEW nudges, and the same-org-staff challenge/workflow
  // eligibility arm only — it does NOT retract an existing internal thread's
  // readability once created (a rollout flag is not a revocation mechanism).
  INTERNAL_TEAM_CONVERSATIONS: "internal_team_conversations",
  // Platform-wide security-model kill switches (owner directive 2026-08-05):
  // each turns one ELEMENT of the security model off across the whole platform
  // — onboarding, Security Centre, step-up challenges, and recovery pathways —
  // so the operator can pull a feature they are not yet happy with during the
  // testing phase. DEFAULT ON (`defaultWhenMissing: true`): a missing row keeps
  // today's behaviour everywhere; only an explicit isEnabled=false row turns
  // the element off. These are testing-phase operator controls, not rollout
  // flags — see FLAG_METADATA for the honest description of what each OFF
  // state costs (Friction Rule clause 4, ledger #62).
  SECURITY_MEMORIES: "security_memories",
  SECURITY_GUARDIANS: "security_guardians",
  // Security-tier-ladder star-category rollout gates (2026-08-31-security-
  // tier-ladder spec, module 01 §The star system; fix round 1 Ruling B).
  // DELIBERATE INVERSE POLARITY from SECURITY_MEMORIES/SECURITY_GUARDIANS
  // above: those are fail-ENABLED platform kill switches (default ON); these
  // are fail-CLOSED rollout flags (default OFF, no defaultWhenMissing) for
  // an UNSHIPPED capability — the knowledge/relationship star categories
  // stay dark until stage 3 wires the draw-mechanics/mutual-challenge
  // ceremonies. A category is actually lit only when BOTH this rollout flag
  // is ON AND the corresponding kill switch above is not OFF — the AND is
  // computed by the caller (shared/lib/security-posture/tier-ladder is
  // pure, no I/O), never by this registry. Not wired to a real gate yet;
  // reserved via a `registry-only: reserved` comment (D-4 pattern, see
  // server/logic/guardian/handshake.ts and server/logic/challenge/
  // seed-subset.ts) so the flag-registry-completeness gate
  // (feature-flags-no-decorative-keys.test.ts) doesn't flag them as dead.
  LADDER_KNOWLEDGE_STARS: "ladder_knowledge_stars",
  LADDER_RELATIONSHIP_STARS: "ladder_relationship_stars",
  FORENSIC_RECOVERY_KEY_QUORUM_INITIATION: "forensic_recovery_key_quorum_initiation",
  FORENSIC_RECOVERY_KEY_GUARDIAN_INITIATION: "forensic_recovery_key_guardian_initiation",
  FORENSIC_RECOVERY_KEY_OFFLINE_CODE_INITIATION: "forensic_recovery_key_offline_code_initiation",
} as const;

export type FlagKey = (typeof FLAG_KEYS)[keyof typeof FLAG_KEYS];

/** Flags the BROWSER evaluates via GET /api/flags (client-side step gating).
 *  The server projects ONLY these keys to authenticated users — every other
 *  flag's state stays operator-visible only. Add a key here when (and only
 *  when) a client `useFeatureFlag` call-site needs it. */
export const CLIENT_VISIBLE_FLAG_KEYS = [
  FLAG_KEYS.ID_VERIFICATION_ONBOARDING,
  FLAG_KEYS.ONBOARDING_SENSORY_VAULT,
  FLAG_KEYS.ONBOARDING_GUARDIAN_CIRCLE,
  FLAG_KEYS.BESPOKE_SEEDING,
  // Pilot-core: client hides nav items + pages for a surface an operator has
  // switched off, mirroring the (always-authoritative) server 404 gate.
  FLAG_KEYS.COMMS_HUB,
  FLAG_KEYS.PAYMENT_REQUESTS,
  FLAG_KEYS.DEALS_MONEY_OPS,
  FLAG_KEYS.BANK_ACCOUNTS,
  FLAG_KEYS.ABA_EXPORT,
  // Security Centre master-passkey surfaces (default-ON opt-out toggle).
  FLAG_KEYS.DEVICE_MASTER_PIN,
  // Module 03's wizard "confirm on respond" radio reads this to stay hidden
  // until module 04's server path actually works (default OFF).
  FLAG_KEYS.GENERIC_CONFIRM_ON_RESPOND_BIND,
  FLAG_KEYS.FREE_FORM_ANSWER_CAPTURE,
  FLAG_KEYS.ORDERED_CHALLENGE_WORKFLOW_CREATION,
  FLAG_KEYS.APPROVAL_TIMELINE_V2,
  // Module 03's Internal-tab compose affordance and module 04's picker
  // "Your team" group both need the client to know this flag's state.
  FLAG_KEYS.INTERNAL_TEAM_CONVERSATIONS,
  // Security-model kill switches: the client hides onboarding steps, Security
  // Centre panels, and recovery surfaces for a disabled element, mirroring the
  // (always-authoritative) server 404 gates.
  FLAG_KEYS.SECURITY_MEMORIES,
  FLAG_KEYS.SECURITY_GUARDIANS,
  // self_registration is deliberately NOT here: its whole client audience is
  // ANONYMOUS (the /auth "Create account" tab), and GET /api/flags is
  // requireAuth-gated — projecting it there would be inert for that audience.
  // The client reads it via the unauthenticated single-boolean endpoint
  // GET /api/auth/registration-availability instead (#1436).
] as const satisfies readonly FlagKey[];

// ---------------------------------------------------------------------------
// Flag metadata — human-readable labels/descriptions + missing-row defaults.
// Split into feature-flags-metadata.ts (300-line modularity rule); re-exported
// here so `@shared/feature-flags` stays the one import path every consumer
// already uses (FlagMetadata, FLAG_METADATA).
// ---------------------------------------------------------------------------

export type { FlagArea, FlagMetadata, FlagSurface } from "./feature-flags-metadata";
export { FLAG_METADATA } from "./feature-flags-metadata";

// ---------------------------------------------------------------------------
// Evaluation context — provided when checking whether a flag is active
// ---------------------------------------------------------------------------

export interface FlagEvaluationContext {
  /** Current deployment environment. */
  environment: "development" | "staging" | "production";
  /** Optional org UUID for org-scoped rollouts. */
  orgId?: string;
}
